<template>
    <footer class="w-full bg-neutral-900 z-20">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <!--Grid-->
            <div class="grid grid-cols-2 lg:grid-cols-4 lg:gap-8 gap-12 pt-14 pb-16 max-w-md mx-auto md:max-w-xl lg:max-w-full">
                <div class="block">
                    <div class="text-xl text-white font-bold mb-7">{{ t('footer.sections.bestfaceswap') }}</div>
                    <ul class="text-md  transition-all duration-500">
                        <li class="mb-6">
                            <NuxtLink :to="localePath('/')" class="text-white/50 hover:text-white">{{ t('footer.links.home') }}</NuxtLink>
                        </li>
                        <li class="mb-6">
                            <NuxtLink :to="localePath('/pricing')" class="text-white/50 hover:text-white duration-300">{{ t('footer.links.pricing') }}
                            </NuxtLink>
                        </li>
                    </ul>
                </div>
                <!--End Col-->
                <div class="block">
                    <div class="text-xl text-white font-bold mb-7">{{ t('footer.sections.products') }}</div>
                    <ul class="text-md  transition-all duration-500">
                        <li class="mb-6">
                            <NuxtLink :to="localePath('/face-swap-video')" class="text-white/50 hover:text-white">
                                {{ t('footer.links.video_face_swap') }}
                            </NuxtLink>
                        </li>
                        <li class="mb-6">
                            <NuxtLink :to="localePath('/face-swap-photo')" class="text-white/50 hover:text-white duration-300">
                                {{ t('footer.links.photo_face_swap') }}</NuxtLink>
                        </li>
                    </ul>
                </div>
                <!--End Col-->
                <div class="block">
                    <div class="text-xl text-white font-bold mb-7">{{ t('footer.sections.resources') }}</div>
                    <ul class="text-md  transition-all duration-500">
                        <li class="mb-6">
                            <NuxtLink :to="localePath('/terms')" class="text-white/50 hover:text-white duration-300">{{ t('footer.links.terms') }}</NuxtLink>
                        </li>
                        <li class="mb-6">
                            <NuxtLink :to="localePath('/policy')" class="text-white/50 hover:text-white duration-300">{{ t('footer.links.privacy') }}
                            </NuxtLink>
                        </li>
                        <li class="mb-6">
                            <NuxtLink :to="localePath('/content-monitoring-policy')" class="text-white/50 hover:text-white duration-300">{{ t('footer.links.content_monitoring') }}</NuxtLink>
                        </li>
                        <li class="mb-6">
                            <NuxtLink to="https://forms.gle/crWH7vpfPhidu6wWA" class="text-white/50 hover:text-white duration-300">{{ t('footer.links.affiliate_program') }}</NuxtLink>
                        </li>
                    </ul>
                </div>
                <!--End Col-->
                <div class="block">
                    <div class="text-xl text-white font-bold mb-7">{{ t('footer.sections.contact') }}</div>
                    <ul class="text-md  transition-all duration-500">

                        <li class="mb-6">
                            <NuxtLink :to="localePath('/contact')" class="text-white/50 hover:text-white duration-300">{{ t('footer.links.contact_us') }}
                            </NuxtLink>
                        </li>
                    </ul>
                </div>
            </div>
            <!--Grid-->
            <div class="pb-32 lg:pb-6 pt-6 border-t border-gray-900">
                <div class="flex items-center  flex-col lg:space-y-0 space-y-8 lg:justify-between lg:flex-row">
                    <a :href="baseUrl" class="flex items-center">
                        <img class="h-8 w-8" src="/idx/bestfaceswap-logo.svg" alt="BestFaceSwap logo">
                        <div class="ml-4 font-semibold text-2xl text-white hidden lg:block">BestFaceSwap</div>
                    </a>

                    <span class="text-white/50 block text-sm font-normal text-center">© {{ t('footer.copyright.year') }}
                        <a :href="baseUrl">BestFaceSwap</a> {{ t('footer.copyright.text') }}</span>

                    <div class="flex mt-4 space-x-4 sm:justify-center sm:mt-0 items-center">
                        <!-- Language Switcher -->
                        <div class="language-switcher mr-6">
                            <v-menu 
                                v-model="isLanguageMenuOpen"
                                location="top"
                                offset="8"
                                class="language-menu"
                            >
                                <template v-slot:activator="{ props }">
                                    <button
                                        v-bind="props"
                                        class="text-white/70 hover:text-white duration-300 flex items-center text-base px-4 py-2 rounded-lg bg-neutral-800/50 hover:bg-neutral-700/60 border border-neutral-600/30"
                                    >
                                        <span class="flag-emoji mr-2 text-lg">{{ currentLanguage.flag }}</span>
                                        <span class="language-name font-medium">{{ currentLanguage.name }}</span>
                                        <svg xmlns="http://www.w3.org/2000/svg" class="ml-2 w-4 h-4 transition-transform duration-300" :class="{ 'rotate-180': isLanguageMenuOpen }" viewBox="0 0 20 20" fill="currentColor">
                                            <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                                        </svg>
                                    </button>
                                </template>

                                <v-list class="language-list !bg-[#2A2D35] !border border-gray-600 rounded-lg shadow-lg min-w-[180px]">
                                    <v-list-item
                                        v-for="language in languages"
                                        :key="language.code"
                                        @click="handleLanguageChange(language.code)"
                                        class="language-item cursor-pointer hover:!bg-[#3A3D45] !text-white text-base"
                                        :class="{ 'active-language !bg-[#4A4D55]': locale === language.code }"
                                    >
                                        <v-list-item-title class="flex items-center">
                                            <span class="flag-emoji mr-3 text-lg">{{ language.flag }}</span>
                                            <span class="font-medium">{{ getFullLanguageName(language.code) }}</span>
                                        </v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>
                        <a class="w-12 h-12 rounded-full flex justify-center items-center bg-neutral-800 hover:bg-neutral-700 active:bg-neutral-600 duration-300" @click.prevent="goContact">
                            <img class=" h-6 w-6" src="/idx/ic_social_mail.svg" alt="Mail">
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </footer>
</template>
<script setup lang="ts">
import { ref, computed } from 'vue';

const { sendEmail } = useEmailSender();
const comm = useCommStore();
const router = useRouter();
const { t, locale } = useI18n();
const localePath = useLocalePath();
const switchLocalePath = useSwitchLocalePath();

// Language switcher logic
const isLanguageMenuOpen = ref(false);

const languages = [
  { code: 'en', name: 'EN', flag: '🇺🇸' },
  { code: 'ja', name: 'JP', flag: '🇯🇵' },
  { code: 'de', name: 'DE', flag: '🇩🇪' },
  { code: 'it', name: 'IT', flag: '🇮🇹' },
  { code: 'fr', name: 'FR', flag: '🇫🇷' },
  { code: 'nl', name: 'NL', flag: '🇳🇱' },
  { code: 'pt', name: 'PT', flag: '🇵🇹' }
];

const currentLanguage = computed(() => {
  return languages.find(lang => lang.code === locale.value) || languages[0];
});

const getFullLanguageName = (code: string) => {
  const fullNames: { [key: string]: string } = {
    en: 'English',
    ja: '日本語',
    de: 'Deutsch',
    it: 'Italiano',
    fr: 'Français',
    nl: 'Nederlands',
    pt: 'Português'
  };
  return fullNames[code] || code;
};

const handleLanguageChange = (langCode: string) => {
  if (locale.value === langCode) {
    isLanguageMenuOpen.value = false;
    return;
  }

  if (process.client) {
    localStorage.setItem('preferred-language', langCode);
    document.cookie = `i18n_redirected=${langCode}; path=/; max-age=31536000`;
  }
  
  // 使用 switchLocalePath 保持当前路由，只改变语言
  const localePath = switchLocalePath(langCode);
  
  if (process.client && localePath) {
    // 使用 router.push 进行软导航，避免页面刷新
    router.push(localePath);
  }
  
  isLanguageMenuOpen.value = false;
};

const browserInfo = computed(() => navigator?.userAgent);

const baseUrl = computed(() => {
    if (process.client) {
        return window.location.hostname.includes('.ai') ? 'https://www.bestfaceswap.ai' : 'https://www.bestfaceswap.net';
    }
    return 'https://www.bestfaceswap.net';
});

const generateAdditionalInfo = () => {
    let info = '';

    // 添加用户 ID 信息
    if (comm.userInfo?.uid) {
        info += `My Account ID: ${comm.userInfo.uid}\n`;
    }

    // 添加浏览器信息
    info += `My Browser: ${browserInfo.value}\n`;

    return info;
};

// 构建完整的邮件内容
const emailContent = computed(() => `Dear Team:

[Please describe your issue or suggestion here]


----------- Other Info -----------
${generateAdditionalInfo()}
`);

// 发送邮件
const onSendEmail = () => {
    sendEmail('', emailContent.value);
};

const goContact = () => {
    router.push(localePath('/contact'));
};
</script>

<style lang="scss" scoped>
.language-switcher {
  .flag-emoji {
    font-family: 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif;
  }
}

.language-list {
  .language-item {
    padding: 12px 16px;
    min-height: 44px;
    
    &:hover {
      background-color: #3A3D45 !important;
    }
    
    &.active-language {
      background-color: #4A4D55 !important;
    }
  }
}
</style>